import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { publicApi } from '@api/client';
import type { Booking, CenterTheme, Service, Staff, TimeSlot } from '@api/types';

export function usePublicBookingByToken(trackingToken: string) {
  return useQuery({
    queryKey: ['public-booking', trackingToken],
    queryFn: () => publicApi.getBookingByTrackingToken(trackingToken) as Promise<Booking>,
    enabled: !!trackingToken,
    retry: false,
  });
}

export function useCancelPublicBooking(trackingToken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => publicApi.cancelByTrackingToken(trackingToken),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['public-booking', trackingToken] }),
  });
}

export function useReschedulePublicBooking(trackingToken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { newStartTime: string; staffId?: string }) =>
      publicApi.rescheduleByTrackingToken(trackingToken, data),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['public-booking', trackingToken] }),
  });
}

export function useCenterTheme(slug: string) {
  return useQuery({
    queryKey: ['center-theme', slug],
    queryFn: () => publicApi.getCenterTheme(slug) as Promise<CenterTheme>,
    enabled: !!slug,
    staleTime: 60_000 * 10,
    // 404 = centre unknown or disabled by admin — no retry, surface the
    // "unavailable" panel immediately.
    retry: false,
  });
}

export function useCenterServices(slug: string) {
  return useQuery({
    queryKey: ['center-services', slug],
    queryFn: () => publicApi.getCenterServices(slug) as Promise<Service[]>,
    enabled: !!slug,
    staleTime: 60_000 * 5,
  });
}

export function useCenterStaff(slug: string, serviceId: string) {
  return useQuery({
    queryKey: ['center-staff', slug, serviceId],
    queryFn: () => publicApi.getCenterStaff(slug, serviceId) as Promise<Staff[]>,
    enabled: !!slug && !!serviceId,
    staleTime: 60_000 * 5,
  });
}

export function useAvailability(slug: string, params: Record<string, string>) {
  const hasRequiredParams = !!params.serviceId && !!params.date;
  return useQuery({
    queryKey: ['availability', slug, params],
    queryFn: () => publicApi.getAvailability(slug, params) as Promise<TimeSlot[]>,
    enabled: !!slug && hasRequiredParams,
    staleTime: 30_000,
  });
}
